'use client';

import { useState } from 'react';

import { USStateObject } from '@domain/states/types';
import { DEFAULT_US_STATE, US_STATES } from '@data/USstates/states';
import { Heading } from '../Heading/Heading';

import { IStateDataSpeciesList } from './helpers/buildStateDataSpecies';
import Select from 'react-select';
import { SearchIcon } from '../Icons/SearchIcon';
import { SpeciesGroupLinks } from './components/SpeciesGroupLinks';
import { Container } from '../Container/Container';
import { NextImageWrapper } from '../NextImage/NextImageWrapper';

interface IProps {
  stateSpeciesData: IStateDataSpeciesList[];
}

// Handle selection change
type StateOption = {
  value: string;
  label: string;
  state: USStateObject;
};

const MAX_TOP_SPECIES_GROUPS = 6;

export const FindUSSpecies = ({ stateSpeciesData }: IProps) => {
  const [selectedState, setSelectedState] = useState<USStateObject>(DEFAULT_US_STATE);

  const stateOptions = US_STATES.map(state => ({
    value: state.stateID,
    label: state.stateName,
    state: state,
  }));

  const handleChange = (selectedOption: StateOption | null) => {
    if (selectedOption) {
      setSelectedState(selectedOption.state);
    } else {
      setSelectedState(DEFAULT_US_STATE);
    }
  };

  return (
    <Container className="mb-sm flex max-w-[900px] flex-col items-center gap-4 sm:mx-auto sm:w-2/3 sm:px-0 xl:max-w-[900px]">
      <Heading variant="sm" as="h2" className="max-w-[440px] text-center">
        Check out the most popular species in your state
      </Heading>

      <Select
        instanceId="us-species-state-select"
        isClearable={true}
        className="bg-sea-foam w-full md:w-11/12"
        classNames={{
          control: state =>
            `p-4 sm:p-2 !border-2 !rounded-lg ${
              state.isFocused ? '!shadow-md !border-tuna' : '!shadow-none !border-twilight'
            }`,
        }}
        options={stateOptions}
        onChange={handleChange}
        placeholder={
          <div className="flex items-center gap-2">
            <SearchIcon />
            Search for US State...
          </div>
        }
      />

      <div className="flex size-full flex-col sm:flex-row sm:gap-4">
        <div className="relative mt-1 flex w-full items-center justify-center sm:mt-0 sm:w-1/2">
          <NextImageWrapper
            wrapperClassName="size-full pb-[100%]"
            imageClassName="aspect-square size-full rounded-t-xl object-cover"
            sizes="(min-width: 600px) 50vw, 100vw"
            src={selectedState.svgGray.src}
            alt={selectedState.stateName}
            fill
            showPlaceholder={false}
          />
        </div>

        {US_STATES.map(state => {
          const stateSpeciesList = stateSpeciesData.find(
            stateDataObj => stateDataObj.stateId === state.stateID,
          );
          return (
            <SpeciesGroupLinks
              key={state.stateID}
              selectedState={state}
              stateSpeciesGroups={
                stateSpeciesList?.speciesGroupings.slice(0, MAX_TOP_SPECIES_GROUPS) ?? []
              }
              className={state.stateID === selectedState.stateID ? 'flex' : 'hidden'}
            />
          );
        })}
      </div>
    </Container>
  );
};
